import { useLanguage } from '../contexts/LanguageContext';
import { useSEO } from '../hooks/useSEO';
import { Link } from 'react-router-dom';
import SharedHeader from './SharedHeader';
import Footer from './Footer';
import { Calendar, Clock, ArrowRight, BookOpen } from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
}

const Blog = () => {
  const { language } = useLanguage();

  useSEO({
    title: language === 'zh' ? '博客与教程 - QRCodeForge' : 'Blog & Tutorials - QRCodeForge',
    description: language === 'zh' 
      ? '学习如何充分利用二维码技术，获取最佳实践和行业见解。QRCodeForge提供全面的二维码营销、技术和应用指南。' 
      : 'Learn how to make the most of QR code technology with best practices and industry insights. QRCodeForge provides comprehensive guides on QR code marketing, technology, and applications.',
    path: '/blog'
  });

  const blogPosts: { [key: string]: BlogPost[] } = {
    en: [
      {
        id: 'restaurant-menu-qr-codes',
        title: 'How to Create QR Codes for Restaurant Menus: Complete Guide',
        excerpt: 'Learn how to create professional QR codes for your restaurant menu, improve customer experience, and reduce printing costs with our comprehensive guide.',
        date: 'January 15, 2025',
        readTime: '8 min read',
        category: 'Restaurant Solutions',
        image: '🍽️'
      },
      {
        id: 'business-card-qr-codes',
        title: 'Business Card QR Code Design: Best Practices and Examples',
        excerpt: 'Discover how to design effective QR codes for business cards that make networking easier and leave a lasting impression on potential clients.',
        date: 'January 12, 2025',
        readTime: '6 min read',
        category: 'Business Tips',
        image: '💼'
      },
      {
        id: 'wifi-qr-code-security',
        title: 'WiFi QR Codes: Setup Guide and Security Best Practices',
        excerpt: 'Complete guide to creating secure WiFi QR codes for your business or home, including security considerations and implementation tips.',
        date: 'January 10, 2025',
        readTime: '7 min read',
        category: 'Technical Guide',
        image: '📶'
      },
      {
        id: 'qr-code-marketing',
        title: '10 Creative Ways to Use QR Codes in Marketing Campaigns',
        excerpt: 'Explore innovative QR code marketing strategies that can boost engagement, track campaign performance, and connect offline to online experiences.',
        date: 'January 8, 2025',
        readTime: '10 min read',
        category: 'Marketing',
        image: '📱'
      },
      {
        id: 'qr-code-technology',
        title: 'Understanding QR Code Technology: How It Works',
        excerpt: 'Deep dive into the technology behind QR codes, including encoding methods, error correction, and why they\'re so reliable and versatile.',
        date: 'January 5, 2025',
        readTime: '12 min read',
        category: 'Technology',
        image: '🔬'
      },
      {
        id: 'retail-qr-solutions',
        title: 'QR Codes in Retail: Enhancing Customer Experience',
        excerpt: 'How retail businesses are using QR codes to provide product information, enable contactless payments, and create interactive shopping experiences.',
        date: 'January 3, 2025',
        readTime: '9 min read',
        category: 'Retail Solutions',
        image: '🏪'
      },
      {
        id: 'real-estate-sales-qr-codes',
        title: 'The Reboot of Real Estate: Boosting Property Sales with QR Codes',
        excerpt: 'QR codes are transforming real estate marketing. Learn how virtual tours, instant property details, and easy agent contact can drive sales.',
        date: 'January 25, 2025',
        readTime: '7 min read',
        category: 'Real Estate',
        image: '🏠'
      },
      {
        id: 'education-classroom-qr-codes',
        title: 'Interactive Learning: 10 Ways Teachers are Using QR Codes',
        excerpt: 'From scavenger hunts to homework help, discover how QR codes are making classrooms more interactive and accessible for students.',
        date: 'January 24, 2025',
        readTime: '8 min read',
        category: 'Education',
        image: '🎓'
      },
      {
        id: 'event-management-qr-codes',
        title: 'Streamlining Events: Ticketing, Networking, and Engagement',
        excerpt: 'Simplify your next event with QR codes. Learn how to use them for contactless entry, digital agendas, and boosting attendee interaction.',
        date: 'January 22, 2025',
        readTime: '6 min read',
        category: 'Events',
        image: '🎟️'
      },
      {
        id: 'healthcare-qr-codes',
        title: 'QR Codes in Healthcare: Improving Patient Care and Safety',
        excerpt: 'QR codes are saving lives by providing instant access to medical history, tracking medications, and streamlining patient check-ins.',
        date: 'January 20, 2025',
        readTime: '9 min read',
        category: 'Healthcare',
        image: '🏥'
      },
      {
        id: 'personal-creative-qr-codes',
        title: 'Beyond Business: 7 Creative Personal Uses for QR Codes',
        excerpt: 'QR codes aren\'t just for companies. Discover fun and practical ways to use them in your daily life, from wedding invites to lost & found tags.',
        date: 'January 18, 2025',
        readTime: '5 min read',
        category: 'Lifestyle',
        image: '💡'
      }
    ],
    zh: [
      {
        id: 'restaurant-menu-qr-codes',
        title: '如何为餐厅菜单创建二维码：完整指南',
        excerpt: '学习如何为您的餐厅菜单创建专业的二维码，改善客户体验，并通过我们的综合指南降低印刷成本。',
        date: '2025年1月15日',
        readTime: '8分钟阅读',
        category: '餐饮解决方案',
        image: '🍽️'
      },
      {
        id: 'business-card-qr-codes',
        title: '名片二维码设计：最佳实践和示例',
        excerpt: '了解如何为名片设计有效的二维码，使社交网络更容易，并给潜在客户留下持久印象。',
        date: '2025年1月12日',
        readTime: '6分钟阅读',
        category: '商业技巧',
        image: '💼'
      },
      {
        id: 'wifi-qr-code-security',
        title: 'WiFi二维码：设置指南和安全最佳实践',
        excerpt: '为您的企业或家庭创建安全WiFi二维码的完整指南，包括安全考虑和实施技巧。',
        date: '2025年1月10日',
        readTime: '7分钟阅读',
        category: '技术指南',
        image: '📶'
      },
      {
        id: 'qr-code-marketing',
        title: '营销活动中使用二维码的10种创意方式',
        excerpt: '探索创新的二维码营销策略，可以提高参与度，跟踪活动表现，并连接线下到线上体验。',
        date: '2025年1月8日',
        readTime: '10分钟阅读',
        category: '营销',
        image: '📱'
      },
      {
        id: 'qr-code-technology',
        title: '理解二维码技术：工作原理',
        excerpt: '深入了解二维码背后的技术，包括编码方法、错误纠正，以及为什么它们如此可靠和多功能。',
        date: '2025年1月5日',
        readTime: '12分钟阅读',
        category: '技术',
        image: '🔬'
      },
      {
        id: 'retail-qr-solutions',
        title: '零售业中的二维码：提升客户体验',
        excerpt: '零售企业如何使用二维码提供产品信息，实现无接触支付，并创造互动购物体验。',
        date: '2025年1月3日',
        readTime: '9分钟阅读',
        category: '零售解决方案',
        image: '🏪'
      },
      {
        id: 'real-estate-sales-qr-codes',
        title: '房地产的重启：利用二维码提升房产销量',
        excerpt: '二维码正在改变房地产营销。了解虚拟导览、即时房产详情和便捷的代理联系如何推动销售。',
        date: '2025年1月25日',
        readTime: '7分钟阅读',
        category: '房地产',
        image: '🏠'
      },
      {
        id: 'education-classroom-qr-codes',
        title: '互动学习：教师使用二维码的10种方式',
        excerpt: '从寻宝游戏到作业帮助，探索二维码如何让课堂对学生来说更加互动和易于访问。',
        date: '2025年1月24日',
        readTime: '8分钟阅读',
        category: '教育',
        image: '🎓'
      },
      {
        id: 'event-management-qr-codes',
        title: '简化活动管理：票务、社交与互动',
        excerpt: '用二维码简化您的下一次活动。了解如何利用它们进行无接触入场、数字议程以及提升参与者互动。',
        date: '2025年1月22日',
        readTime: '6分钟阅读',
        category: '活动',
        image: '🎟️'
      },
      {
        id: 'healthcare-qr-codes',
        title: '医疗中的二维码：改善患者护理与安全',
        excerpt: '二维码正在通过提供病史即时访问、追踪药物和简化患者登记来挽救生命。',
        date: '2025年1月20日',
        readTime: '9分钟阅读',
        category: '医疗',
        image: '🏥'
      },
      {
        id: 'personal-creative-qr-codes',
        title: '商业之外：二维码的7个创意个人用法',
        excerpt: '二维码不仅仅适用于公司。发现它们在您日常生活中的有趣和实用方式，从婚礼请柬到失物招领标签。',
        date: '2025年1月18日',
        readTime: '5分钟阅读',
        category: '生活方式',
        image: '💡'
      }
    ]
  };

  const currentPosts = blogPosts[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SharedHeader />

      {/* 动态背景元素 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-10 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      <div className="relative">
        {/* Header */}
        <header className="pt-20 pb-12">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {language === 'zh' ? '博客与教程' : 'Blog & Tutorials'}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {language === 'zh' 
                ? '学习如何充分利用二维码技术，获取最佳实践和行业见解'
                : 'Learn how to make the most of QR code technology with best practices and industry insights'
              }
            </p>
          </div>
        </header>

        {/* Newsletter Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/10">
            <h2 className="text-3xl font-bold text-white mb-4">
              {language === 'zh' ? '订阅更新' : 'Stay Updated'}
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              {language === 'zh' 
                ? '订阅我们的通讯，获取最新的二维码营销技巧和行业动态。'
                : 'Subscribe to our newsletter to get the latest QR code marketing tips and industry updates.'
              }
            </p>
            <form className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder={language === 'zh' ? '输入您的邮箱' : 'Enter your email'}
                className="px-6 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all"
              />
              <button
                type="submit"
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all transform hover:scale-105"
              >
                {language === 'zh' ? '订阅' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>
      </section>

        {/* Blog Posts Grid */}
        <div className="max-w-6xl mx-auto px-4 pb-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {currentPosts.map((post) => (
              <Link
                key={post.id}
                to={`/${language}/blog/${post.id}`}
                className="group bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden hover:bg-white/15 transition-all duration-300 hover:scale-105"
              >
                <div className="p-8">
                  <div className="text-5xl mb-4">{post.image}</div>
                  
                  <div className="mb-3">
                    <span className="text-xs font-semibold text-blue-300 uppercase tracking-wide">
                      {post.category}
                    </span>
                  </div>

                  <h2 className="text-xl font-bold text-white mb-3 group-hover:text-blue-300 transition-colors">
                    {post.title}
                  </h2>

                  <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{post.date}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Coming Soon Section */}
          <div className="mt-12 bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              {language === 'zh' ? '更多内容即将推出' : 'More Content Coming Soon'}
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              {language === 'zh'
                ? '我们定期发布新的教程、指南和行业见解。订阅我们的更新，不要错过任何内容。'
                : 'We regularly publish new tutorials, guides, and industry insights. Subscribe to our updates to never miss out.'
              }
            </p>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default Blog;
